import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import ChartTooltip from '../chart-tooltip/chart-tooltip.js';
import DownloadAsPNGButton from '../download-png-btn/download-as-png-btn.js';
import DownloadAsExcelButton from '../download-as-excel-btn/download-as-excel-btn.js';
import { formatFilters } from '../../utils/format-filters.js';
import { observeElementSize } from '../../utils/observe-element-size.js';

import './map.css';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const Map = (props) => {
  const {
    id,
    data,
    geodata,
    click,
    getValueFunction,
    getColorsFunction,
    getTooltipTextFunction,
    fileName,
    columns,
    filters,
    title,
    mapHeight = undefined,
  } = props;

  const containerRef = useRef(null);
  const [tooltipTop, setTooltipTop] = useState('auto');
  const [tooltipLeft, setTooltipLeft] = useState('auto');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState('');
  const [tooltipValue, setTooltipValue] = useState('');
  const margin = { top: 20, right: 20, bottom: 20, left: 20 };

  const applyFeatureColors = () => {
    if (!data || !geodata?.features?.length) {
      return;
    }

    const colors = getColorsFunction(data);
    const colorScale = d3.scaleThreshold().domain(colors.trashold).range(colors.range);

    d3.selectAll(`#chart-${id} path`).attr('fill', function (feature) {
      const regionEntry = data?.[feature.properties.shapeName];
      const value = getValueFunction(regionEntry);
      const tooltipText = getTooltipTextFunction(regionEntry);
      feature.total = tooltipText || 0;
      return colorScale(value || 0);
    });
  };

  useEffect(() => {
    const host = containerRef.current;
    if (!host || !geodata?.features?.length) {
      return undefined;
    }

    const renderMap = () => {
      const bounds = host.getBoundingClientRect();
      const availableWidth = clamp(bounds.width - margin.left - margin.right, 280, 760);
      const height = mapHeight ?? (window.innerWidth > 900 ? 520 : 360);

      d3.select(host).selectAll('svg').remove();

      const svg = d3
        .select(host)
        .append('svg')
        .attr('width', availableWidth + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('viewBox', `0 0 ${availableWidth + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      const projection = d3.geoMercator().fitSize([availableWidth, height], geodata);
      const path = d3.geoPath().projection(projection);

      function mouseover(event, feature) {
        setTooltipTitle(feature.properties.shapeName);
        setTooltipValue(feature.total);
        event.currentTarget.style.stroke = 'rgba(255,255,255,0.95)';
        event.currentTarget.style.strokeWidth = '1.5px';
        setTooltipVisible(true);
      }

      function mousemove(event) {
        setTooltipTop(`${event.offsetY}px`);
        setTooltipLeft(`${event.offsetX}px`);
      }

      function mouseleave(event) {
        event.currentTarget.style.stroke = 'rgba(255,255,255,0.24)';
        event.currentTarget.style.strokeWidth = '1px';
        setTooltipVisible(false);
      }

      svg
        .append('g')
        .selectAll('path')
        .data(geodata.features)
        .join('path')
        .attr('d', path)
        .attr('class', 'map-region')
        .on('click', (event, feature) => {
          if (click) {
            click(event, feature);
          }
        })
        .on('mouseenter', mouseover)
        .on('mousemove', mousemove)
        .on('mouseleave', mouseleave)
        .attr('fill', 'rgba(255,255,255,0.08)')
        .attr('stroke', 'rgba(255,255,255,0.24)')
        .attr('stroke-width', 1);

      applyFeatureColors();
    };

    renderMap();

    const stopObserving = observeElementSize(host, renderMap);

    return () => {
      stopObserving();
      d3.select(host).selectAll('svg').remove();
    };
  }, [click, data, geodata, getColorsFunction, getTooltipTextFunction, getValueFunction, id, mapHeight, margin.bottom, margin.left, margin.right, margin.top]);

  useEffect(() => {
    applyFeatureColors();
  }, [data, id]);

  return (
    <div className="chart-wrapper map-chart-wrapper">
      <div className="chart-container map-chart-container" id={`chart-${id}`} ref={containerRef}>
        <ChartTooltip
          top={tooltipTop}
          left={tooltipLeft}
          title={tooltipTitle}
          visible={tooltipVisible}
          value={tooltipValue}
        />
      </div>
      <div className="download-btns">
        <DownloadAsExcelButton data={data} columns={columns} fileName={fileName} />
        <DownloadAsPNGButton
          chartId={`chart-${id}`}
          fileName="map.png"
          metadata={{
            title,
            filters: formatFilters(filters),
            downloadDate: new Date().toLocaleDateString(),
            websiteName: 'JozaniAI for Tourism',
          }}
        />
      </div>
    </div>
  );
};

Map.propTypes = {};

Map.defaultProps = {};

export default Map;
